import React, { useState, useEffect } from 'react';
import { Card, Typography, Button, Switch, Space, Input, message, Tag, Row, Col, Tooltip } from 'antd';
import { SaveOutlined, ReloadOutlined, DragOutlined, MessageOutlined, TeamOutlined, CompassOutlined, UserOutlined } from '@ant-design/icons';
import api from '../api';

const { Title, Text } = Typography;

const iconMap = {
  message: <MessageOutlined />,
  contact: <TeamOutlined />,
  discover: <CompassOutlined />,
  mine: <UserOutlined />,
};

function TabConfigPage() {
  const [tabs, setTabs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadTabs = async () => {
    setLoading(true);
    try {
      const { config } = await api.get('/admin/tab-config');
      setTabs(config || defaultTabs());
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadTabs(); }, []);

  const defaultTabs = () => [
    { key: 'session', label: '消息', icon: 'message', order: 1, visible: true },
    { key: 'contact', label: '通讯录', icon: 'contact', order: 2, visible: true },
    { key: 'discover', label: '发现', icon: 'discover', order: 3, visible: true },
    { key: 'mine', label: '我', icon: 'mine', order: 4, visible: true },
  ];

  const handleToggle = (key) => {
    setTabs(tabs.map(t => t.key === key ? { ...t, visible: !t.visible } : t));
  };

  const handleLabelChange = (key, value) => {
    setTabs(tabs.map(t => t.key === key ? { ...t, label: value } : t));
  };

  const handleIconChange = (key, value) => {
    setTabs(tabs.map(t => t.key === key ? { ...t, icon: value } : t));
  };

  const handleMove = (index, direction) => {
    const newTabs = [...tabs];
    const target = index + direction;
    if (target < 0 || target >= newTabs.length) return;
    [newTabs[index], newTabs[target]] = [newTabs[target], newTabs[index]];
    newTabs.forEach((t, i) => t.order = i + 1);
    setTabs(newTabs);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/admin/tab-config', tabs);
      message.success('Tab 配置已保存');
    } catch (e) {
      message.error('保存失败');
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <Title level={4}>底部 Tab 配置</Title>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={loadTabs}>刷新</Button>
          <Button type="primary" icon={<SaveOutlined />} onClick={handleSave} loading={saving} style={{ background: '#07C160' }}>保存配置</Button>
        </Space>
      </div>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        配置 App 底部导航栏的 Tab 项。启用/禁用可实时控制客户端底部导航栏展示。
      </Text>

      <Row gutter={16}>
        {tabs.sort((a, b) => a.order - b.order).map((tab, index) => (
          <Col span={6} key={tab.key} style={{ marginBottom: 16 }}>
            <Card
              size="small"
              title={
                <Space>
                  <DragOutlined />
                  <Text strong>Tab #{tab.order}</Text>
                </Space>
              }
              extra={
                <Switch size="small" checked={tab.visible} onChange={() => handleToggle(tab.key)} checkedChildren="显示" unCheckedChildren="隐藏" />
              }
              style={{ opacity: tab.visible ? 1 : 0.5, border: tab.visible ? '1px solid #d9d9d9' : '1px dashed #d9d9d9' }}
            >
              <Space direction="vertical" style={{ width: '100%' }} size={8}>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>Tab Key</Text>
                  <br />
                  <Tag color="blue">{tab.key}</Tag>
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>显示名称</Text>
                  <br />
                  <Input size="small" value={tab.label} onChange={(e) => handleLabelChange(tab.key, e.target.value)} style={{ width: '100%' }} />
                </div>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>图标</Text>
                  <br />
                  <Input size="small" value={tab.icon} onChange={(e) => handleIconChange(tab.key, e.target.value)} style={{ width: '100%' }} prefix={iconMap[tab.icon] || <MessageOutlined />} />
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Tooltip title="上移">
                    <Button size="small" disabled={index === 0} onClick={() => handleMove(index, -1)}>↑</Button>
                  </Tooltip>
                  <Tooltip title="下移">
                    <Button size="small" disabled={index === tabs.length - 1} onClick={() => handleMove(index, 1)}>↓</Button>
                  </Tooltip>
                </div>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>

      <Card size="small" style={{ background: '#f6ffed', border: '1px solid #b7eb8f' }}>
        <Text type="secondary">
          💡 提示：修改后点击"保存配置"，配置将实时下发到已连接的客户端。隐藏的 Tab 将在 App 底部导航栏中消失。
        </Text>
      </Card>
    </>
  );
}

export default TabConfigPage;
